import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Save, Loader2, ImagePlus, Building2, Zap, LayoutDashboard, Inbox, Plus, Trash2, Type, Images } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { DEFAULT_CONTENT, resolveImg } from "@/lib/content";
import { EV_IMAGES } from "@/lib/siteContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-[#0B192C] placeholder:text-slate-400 outline-none focus:border-amber-500 focus:ring-4 focus:ring-amber-400/20 transition-all";
const labelCls = "block text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-1.5";

const TABS = [
  { id: "carregadores", label: "Carregadores", icon: Zap },
  { id: "empresa", label: "Empresa", icon: Building2 },
  { id: "plataforma", label: "Plataforma", icon: LayoutDashboard },
  { id: "textos", label: "Textos do site", icon: Type },
  { id: "imagens", label: "Imagens", icon: Images },
  { id: "pedidos", label: "Pedidos recebidos", icon: Inbox },
];

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
};

export default function AdminPage() {
  useSeo("Administração | HB Solar", "Painel administrativo do site HB Solar.");
  const navigate = useNavigate();
  const token = localStorage.getItem("hb_admin_token");
  const [tab, setTab] = useState("carregadores");
  const [content, setContent] = useState(null);
  const [chargerIdx, setChargerIdx] = useState(0);
  const [leads, setLeads] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    const headers = { Authorization: `Bearer ${token}` };
    axios.get(`${API}/auth/me`, { headers }).catch(() => {
      localStorage.removeItem("hb_admin_token");
      navigate("/admin/login");
    });
    axios.get(`${API}/content`).then(({ data }) => {
      setContent({
        company: { ...DEFAULT_CONTENT.company, ...(data.company || {}) },
        chargers: Array.isArray(data.chargers) && data.chargers.length ? data.chargers : DEFAULT_CONTENT.chargers,
        platformCommercial: data.platformCommercial || DEFAULT_CONTENT.platformCommercial,
        textos: { ...DEFAULT_CONTENT.textos, ...(data.textos || {}) },
        imagens: { ...DEFAULT_CONTENT.imagens, ...(data.imagens || {}) },
      });
    });
    axios.get(`${API}/admin/leads`, { headers }).then(({ data }) => setLeads(data)).catch(() => {});
  }, [token, navigate]);

  if (!content) {
    return (
      <section className="min-h-screen bg-[#FAFAF9] pt-[76px] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
      </section>
    );
  }

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/content`, content, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Alterações salvas e já visíveis no site!");
    } catch (e) {
      toast.error("Não foi possível salvar. Tente novamente.");
    }
    setSaving(false);
  };

  const uploadFile = async (file) => {
    if (!file) return null;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Imagem enviada! Clique em Salvar para publicar.");
      return data.path;
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Falha no upload da imagem.");
      return null;
    } finally {
      setUploading(false);
    }
  };

  const uploadPhoto = async (file) => {
    const path = await uploadFile(file);
    if (path) updateCharger("foto", path);
  };

  const updateCharger = (key, value) => {
    const chargers = content.chargers.map((c, i) => (i === chargerIdx ? { ...c, [key]: value } : c));
    setContent({ ...content, chargers });
  };

  const addCharger = () => {
    const novo = {
      id: `hb-charger-${Date.now()}`,
      nome: "Novo carregador",
      linha: "Estação de recarga para veículos elétricos",
      foto: EV_IMAGES.carregador,
      potencia: "",
      conectores: "",
      voltagem: "",
      tensao: "",
      comunicacao: "OCPP (conforme configuração contratada)",
      potenciaConfiguravel: "",
      caracteristicas: [],
      resumo: "",
    };
    setContent({ ...content, chargers: [...content.chargers, novo] });
    setChargerIdx(content.chargers.length);
    toast.success("Novo carregador criado — preencha os dados e clique em Salvar.");
  };

  const removeCharger = () => {
    if (content.chargers.length <= 1) {
      toast.error("O catálogo precisa ter pelo menos um carregador.");
      return;
    }
    const chargers = content.chargers.filter((_, i) => i !== chargerIdx);
    setContent({ ...content, chargers });
    setChargerIdx(0);
    toast.success("Modelo removido — clique em Salvar para confirmar.");
  };

  const logout = () => {
    localStorage.removeItem("hb_admin_token");
    navigate("/admin/login");
  };

  const ch = content.chargers[chargerIdx];

  return (
    <section data-testid="admin-dashboard" className="min-h-screen bg-[#FAFAF9] pt-[76px]">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-amber-500">Painel administrativo</p>
            <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-[#0B192C]">
              Editar conteúdo do site
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-save-button"
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-full bg-amber-400 hover:bg-amber-300 text-[#0B192C] font-extrabold px-6 py-3 text-sm transition-colors disabled:opacity-60"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Salvar alterações
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-5 py-3 text-sm font-bold text-slate-600 hover:border-red-300 hover:text-red-600 transition-colors"
            >
              <LogOut className="h-4 w-4" /> Sair
            </button>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${
                tab === t.id ? "bg-[#0B192C] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-amber-400"
              }`}
            >
              <t.icon className="h-4 w-4" />
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "carregadores" && (
            <div className="grid lg:grid-cols-[220px_1fr] gap-6">
              <div className="space-y-2">
                {content.chargers.map((c, i) => (
                  <button
                    key={c.id || i}
                    data-testid={`admin-charger-select-${i}`}
                    onClick={() => setChargerIdx(i)}
                    className={`w-full rounded-2xl border px-4 py-3.5 text-left text-sm font-bold transition-colors ${
                      i === chargerIdx ? "border-amber-400 bg-amber-50 text-[#0B192C]" : "border-slate-200 bg-white text-slate-600 hover:border-amber-300"
                    }`}
                  >
                    {c.nome}
                  </button>
                ))}
                <button
                  data-testid="admin-add-charger-button"
                  onClick={addCharger}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-amber-300 px-4 py-3.5 text-sm font-bold text-amber-600 hover:bg-amber-50 transition-colors"
                >
                  <Plus className="h-4 w-4" /> Adicionar carregador
                </button>
              </div>
              {ch && (
                <div className="rounded-3xl border border-slate-200 bg-white p-7 space-y-4">
                  <div className="flex flex-wrap items-center gap-5">
                    {ch.foto ? (
                      <img src={resolveImg(ch.foto)} alt={ch.nome} className="h-24 w-24 rounded-2xl border border-slate-200 object-contain bg-white" />
                    ) : (
                      <span className="flex h-24 w-24 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50">
                        <Zap className="h-8 w-8 text-slate-300" />
                      </span>
                    )}
                    <label
                      data-testid="admin-upload-photo"
                      className={`inline-flex cursor-pointer items-center gap-2 rounded-full border-2 border-dashed border-slate-300 px-5 py-3 text-sm font-bold text-slate-600 hover:border-amber-400 hover:text-amber-600 transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`}
                    >
                      {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                      {uploading ? "Enviando..." : "Trocar foto"}
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} />
                    </label>
                    <button
                      data-testid="admin-delete-charger-button"
                      onClick={removeCharger}
                      className="inline-flex items-center gap-2 rounded-full border border-red-200 px-5 py-3 text-sm font-bold text-red-500 hover:bg-red-50 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" /> Remover modelo
                    </button>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>Nome do modelo</label>
                      <input data-testid="admin-charger-nome" className={inputCls} value={ch.nome} onChange={(e) => updateCharger("nome", e.target.value)} />
                    </div>
                    <div>
                      <label className={labelCls}>Linha / categoria</label>
                      <input className={inputCls} value={ch.linha || ""} onChange={(e) => updateCharger("linha", e.target.value)} />
                    </div>
                    <div>
                      <label className={labelCls}>Potência</label>
                      <input className={inputCls} value={ch.potencia || ""} onChange={(e) => updateCharger("potencia", e.target.value)} placeholder="Ex: 60–480 kW" />
                    </div>
                    <div>
                      <label className={labelCls}>Conectores</label>
                      <input className={inputCls} value={ch.conectores || ""} onChange={(e) => updateCharger("conectores", e.target.value)} placeholder="Ex: 2 × CCS2" />
                    </div>
                    <div>
                      <label className={labelCls}>Voltagem</label>
                      <input className={inputCls} value={ch.voltagem || ""} onChange={(e) => updateCharger("voltagem", e.target.value)} placeholder="Ex: 380 V ±10% AC" />
                    </div>
                    <div>
                      <label className={labelCls}>Tensão de saída</label>
                      <input className={inputCls} value={ch.tensao || ""} onChange={(e) => updateCharger("tensao", e.target.value)} placeholder="Ex: 150–1000 V DC" />
                    </div>
                    <div>
                      <label className={labelCls}>Comunicação</label>
                      <input className={inputCls} value={ch.comunicacao || ""} onChange={(e) => updateCharger("comunicacao", e.target.value)} />
                    </div>
                    <div>
                      <label className={labelCls}>Potência configurável</label>
                      <input className={inputCls} value={ch.potenciaConfiguravel || ""} onChange={(e) => updateCharger("potenciaConfiguravel", e.target.value)} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Resumo</label>
                    <textarea rows={2} className={inputCls} value={ch.resumo || ""} onChange={(e) => updateCharger("resumo", e.target.value)} />
                  </div>
                  <div>
                    <label className={labelCls}>Características (uma por linha)</label>
                    <textarea
                      data-testid="admin-charger-caracteristicas"
                      rows={6}
                      className={inputCls}
                      value={(ch.caracteristicas || []).join("\n")}
                      onChange={(e) => updateCharger("caracteristicas", e.target.value.split("\n").filter((l) => l.trim()))}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === "empresa" && (
            <div className="rounded-3xl border border-slate-200 bg-white p-7 grid sm:grid-cols-2 gap-4 max-w-3xl">
              {[
                ["cnpj", "CNPJ", "admin-company-cnpj"],
                ["endereco", "Endereço", "admin-company-endereco"],
                ["regioes", "Regiões atendidas", "admin-company-regioes"],
                ["telefone", "Telefone", null],
                ["email", "E-mail", null],
              ].map(([key, label, testId]) => (
                <div key={key}>
                  <label className={labelCls}>{label}</label>
                  <input
                    data-testid={testId || undefined}
                    className={inputCls}
                    value={content.company[key] || ""}
                    onChange={(e) => setContent({ ...content, company: { ...content.company, [key]: e.target.value } })}
                  />
                </div>
              ))}
            </div>
          )}

          {tab === "plataforma" && (
            <div className="rounded-3xl border border-slate-200 bg-white p-7 max-w-3xl">
              <label className={labelCls}>Valores e condições comerciais da plataforma</label>
              <textarea
                data-testid="admin-platform-commercial"
                rows={4}
                className={inputCls}
                value={content.platformCommercial}
                onChange={(e) => setContent({ ...content, platformCommercial: e.target.value })}
              />
            </div>
          )}

          {tab === "textos" && (
            <div className="rounded-3xl border border-slate-200 bg-white p-7 max-w-3xl space-y-4" data-testid="admin-textos-section">
              {[
                ["heroEvTitulo", "Título principal — HB Charger (início)"],
                ["heroEvSubtitulo", "Subtítulo — HB Charger"],
                ["homeServicosTitulo", "Título da seção de serviços"],
                ["homeCtaTitulo", "Título do chamado 'Simule seu projeto'"],
                ["homeCtaTexto", "Texto do chamado 'Simule seu projeto'"],
                ["solarTitulo", "Título principal — Energia Solar"],
                ["solarSubtitulo", "Subtítulo — Energia Solar"],
                ["gatewaySolarTitulo", "Tela de entrada — título Energia Solar"],
                ["gatewaySolarTexto", "Tela de entrada — texto Energia Solar"],
                ["gatewayChargerTitulo", "Tela de entrada — título Carregadores"],
                ["gatewayChargerTexto", "Tela de entrada — texto Carregadores"],
              ].map(([key, label]) => (
                <div key={key}>
                  <label className={labelCls}>{label}</label>
                  <textarea
                    rows={2}
                    className={inputCls}
                    value={content.textos?.[key] || ""}
                    onChange={(e) =>
                      setContent({ ...content, textos: { ...content.textos, [key]: e.target.value } })
                    }
                  />
                </div>
              ))}
            </div>
          )}

          {tab === "imagens" && (
            <div className="grid sm:grid-cols-2 gap-5 max-w-4xl" data-testid="admin-imagens-section">
              {[
                ["heroEv", "Imagem principal — HB Charger (início)"],
                ["solarHero", "Imagem principal — Energia Solar"],
                ["gatewaySolar", "Tela de entrada — imagem Energia Solar"],
                ["gatewayCharger", "Tela de entrada — imagem Carregadores"],
              ].map(([key, label]) => (
                <div key={key} className="rounded-3xl border border-slate-200 bg-white p-5">
                  <p className={labelCls}>{label}</p>
                  <div className="flex h-40 items-center justify-center overflow-hidden rounded-2xl border border-slate-100 bg-slate-50">
                    {content.imagens?.[key] ? (
                      <img src={resolveImg(content.imagens[key])} alt={label} className="h-full w-full object-cover" />
                    ) : (
                      <ImagePlus className="h-8 w-8 text-slate-300" />
                    )}
                  </div>
                  <label
                    className={`mt-4 inline-flex cursor-pointer items-center gap-2 rounded-full border-2 border-dashed border-slate-300 px-5 py-2.5 text-sm font-bold text-slate-600 hover:border-amber-400 hover:text-amber-600 transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`}
                  >
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                    {uploading ? "Enviando..." : "Trocar imagem"}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      data-testid={`admin-image-upload-${key}`}
                      onChange={async (e) => {
                        const path = await uploadFile(e.target.files?.[0]);
                        if (path) {
                          setContent({ ...content, imagens: { ...content.imagens, [key]: path } });
                        }
                      }}
                    />
                  </label>
                </div>
              ))}
            </div>
          )}

          {tab === "pedidos" && (
            <div data-testid="admin-leads-table" className="rounded-3xl border border-slate-200 bg-white overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#0B192C] text-left text-white">
                      <th className="px-5 py-3.5 font-bold">Data</th>
                      <th className="px-5 py-3.5 font-bold">Tipo</th>
                      <th className="px-5 py-3.5 font-bold">Nome</th>
                      <th className="px-5 py-3.5 font-bold">WhatsApp</th>
                      <th className="px-5 py-3.5 font-bold">Cidade</th>
                      <th className="px-5 py-3.5 font-bold">Detalhes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.length === 0 && (
                      <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400">Nenhum pedido ainda.</td></tr>
                    )}
                    {leads.map((l) => (
                      <tr key={l.id} className="border-t border-slate-100 hover:bg-amber-50/40">
                        <td className="px-5 py-3 text-slate-500 whitespace-nowrap">{fmtDate(l.created_at)}</td>
                        <td className="px-5 py-3">
                          <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-bold text-amber-700">{l.tipo}</span>
                        </td>
                        <td className="px-5 py-3 font-bold text-[#0B192C]">{l.nome}</td>
                        <td className="px-5 py-3 text-slate-600">{l.telefone}</td>
                        <td className="px-5 py-3 text-slate-600">{l.cidade}</td>
                        <td className="px-5 py-3 text-xs text-slate-500 max-w-[260px]">
                          {[l.empresa, l.tipo_empreendimento, l.potencia_interesse, l.quantidade, l.potencia_desejada, l.valor_conta ? `Conta: R$ ${l.valor_conta}` : null, l.mensagem].filter(Boolean).join(" · ")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
